/**
 * Full Studio playlist replace (Campanha → Publicar) vs native Media3 same-URI skip.
 *
 * Kotlin `load skip (same uri)` leaves Exo mid-clip while the scheduler rematches at 0.
 * Prefer `present({ restart: true })` (seek without tearing the decoder). Old APKs ignore
 * `restart`; JS then falls back to release + present.
 */

import type { THybridPlaybackKind, THybridPlaybackStatus } from '@/helpers/androidHybridPlayback'

/** After a forced seek, treat this much residual position as “started at 0”. */
export const NATIVE_HYBRID_RESTART_AT_START_SLACK_MS = 400

export function nativeHybridSameUriNeedsRestart(
  lastUri: string | null,
  nextUri: string,
  restartArmed: boolean
): boolean {
  return restartArmed && lastUri != null && lastUri === nextUri
}

export function nativeHybridRestartLandedAtStart(positionMs: number | null | undefined): boolean {
  if (positionMs == null || !Number.isFinite(positionMs) || positionMs < 0) {
    return false
  }
  return positionMs <= NATIVE_HYBRID_RESTART_AT_START_SLACK_MS
}

export function hybridSlotPositionMs(
  status: { slot0PositionMs?: number, slot1PositionMs?: number } | null | undefined,
  slot: 0 | 1
): number | null {
  if (status == null) {
    return null
  }
  const ms = slot === 1 ? status.slot1PositionMs : status.slot0PositionMs
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : null
}

export async function presentNativeHybridRestartOrRemount(opts: {
  slot: 0 | 1
  uri: string
  /** Defaults to muted. Pass `false` only when the on-air facet is `unmuted`. */
  muted?: boolean
  /** Media3 resize for this clip. Omitted leaves the slot's current mode. */
  fit?: 'contain' | 'cover'
  present: (
    slot: 0 | 1,
    uri: string,
    kind?: THybridPlaybackKind,
    presentOpts?: { restart?: boolean, muted?: boolean, fit?: 'contain' | 'cover' }
  ) => Promise<THybridPlaybackStatus | null>
  release: (slot?: 0 | 1) => Promise<THybridPlaybackStatus | null>
}): Promise<{ status: THybridPlaybackStatus | null, remounted: boolean }> {
  const muted = opts.muted !== false
  const fit = opts.fit === 'contain' || opts.fit === 'cover' ? opts.fit : undefined
  const seeked = await opts.present(
    opts.slot,
    opts.uri,
    'video',
    fit == null ? { restart: true, muted } : { restart: true, muted, fit }
  )
  if (nativeHybridRestartLandedAtStart(hybridSlotPositionMs(seeked, opts.slot))) {
    return { status: seeked, remounted: false }
  }
  await opts.release(opts.slot)
  const remounted = await opts.present(
    opts.slot,
    opts.uri,
    'video',
    fit == null ? { muted } : { muted, fit }
  )
  return { status: remounted, remounted: true }
}
